/**
 * KAVORRI — cart drawer enhancements (v2)
 *
 * 1. Overlay mode: #cart-drawer always opens as a modal overlay (never squeezes the page),
 *    so the KAVORRI header / mega menu keep their exact width and position.
 * 2. <kavorri-cart-pairs>: "Pairs well with", powered by the KAVORRI Smart Complete the Look
 *    scoring model (same metafields, same weights), extended to score against the whole cart.
 * 3. Quick add through the native cart events, so the drawer, totals and header counts update
 *    through the theme's own Section Rendering API flow.
 * 4. Mobile "Order summary / See more" state that survives section morphs.
 */
import { lockScroll, fetchConfig } from '@theme/utilities';
import { removeTrapFocus } from '@theme/focus';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
 
const DRAWER_ID = 'cart-drawer';
const SECTION_ID = 'cart-drawer-section';
 
/* ==========================================================================
   1. Overlay mode (desktop included)
   ========================================================================== */
 
const getDrawer = () => /** @type {any} */ (document.getElementById(DRAWER_ID));
 
function forceModal() {
  const drawer = getDrawer();
  if (!drawer || !drawer.hasAttribute('data-kc-overlay') || !drawer.hasAttribute('open')) return;
  const panel = drawer.querySelector('dialog');
  if (!panel || panel.matches(':modal')) return;
 
  // theme-drawer opened us as a non-modal sidebar (≥ 990px). Re-open as a true modal:
  // native backdrop, native focus containment, scroll lock — no page squeeze.
  removeTrapFocus();
  if (panel.open) panel.close();
  lockScroll(panel);
  panel.showModal();
  /** @type {HTMLElement | null} */ (panel.querySelector('.theme-drawer__close-button'))?.focus({ preventScroll: true });
  releaseSqueeze();
}
 
function releaseSqueeze() {
  const wrapper = document.querySelector('.page-wrapper');
  if (!wrapper) return;
  const squeezingDrawerOpen = document.querySelector('theme-drawer[open]:not([data-kc-overlay])');
  if (!squeezingDrawerOpen) wrapper.classList.remove('page-wrapper--drawer-open');
}
 
function initOverlay() {
  const drawer = getDrawer();
  if (!drawer) return;
 
  // A drawer restored as "open" from a previous page (sidebar restore) is closed:
  // the cart overlay should never re-open by itself after navigation.
  try {
    const stored = (sessionStorage.getItem('theme-drawer-open') || '').split(',').filter((id) => id && id !== DRAWER_ID);
    if (stored.length) sessionStorage.setItem('theme-drawer-open', stored.join(','));
    else sessionStorage.removeItem('theme-drawer-open');
  } catch (_) { /* storage unavailable */ }
 
  customElements.whenDefined('theme-drawer').then(() => {
    const current = getDrawer();
    if (current?.hasAttribute('open')) {
      const panel = current.querySelector('dialog');
      if (panel && !panel.matches(':modal')) current.close?.();
    }
  });
 
  // Runs synchronously inside ThemeDrawer.open(), before the next paint.
  document.addEventListener('theme-drawer:open', (event) => {
    if (event.target instanceof HTMLElement && event.target.id === DRAWER_ID) forceModal();
  });
 
  // ThemeDrawer switches to sidebar mode when the viewport crosses 990px — switch back.
  window.matchMedia('(max-width: 989px)').addEventListener('change', () => requestAnimationFrame(forceModal));
 
  // The squeeze class is added by a document listener in snippets/theme-drawer.liquid.
  const wrapper = document.querySelector('.page-wrapper');
  if (wrapper) {
    new MutationObserver(() => {
      if (wrapper.classList.contains('page-wrapper--drawer-open') && getDrawer()?.hasAttribute('open')) releaseSqueeze();
    }).observe(wrapper, { attributes: true, attributeFilter: ['class'] });
  }
}
 
/* ==========================================================================
   2. Mobile "Order summary / See more" (state kept on <html>, survives morphs)
   ========================================================================== */
 
function syncSummaryAria() {
  const open = document.documentElement.hasAttribute('data-kc-summary-open');
  document.querySelectorAll('[data-kc-summary-toggle]').forEach((btn) => btn.setAttribute('aria-expanded', String(open)));
}
 
document.addEventListener('click', (event) => {
  const toggle = event.target instanceof Element ? event.target.closest('[data-kc-summary-toggle]') : null;
  if (!toggle) return;
  event.preventDefault();
  const open = document.documentElement.toggleAttribute('data-kc-summary-open');
  syncSummaryAria();
  if (open) {
    const input = /** @type {HTMLInputElement | null} */ (document.querySelector('#cart-drawer .cart-discount__input'));
    input?.scrollIntoView({ block: 'nearest' });
  }
});
 
/* ==========================================================================
   3. KAVORRI header badge sync (custom header is outside the native cart-icon)
   ========================================================================== */
 
function renderHeaderCount(count) {
  if (typeof count !== 'number' || Number.isNaN(count)) return;
  const menu = document.querySelector('kavorri-menu');
  if (!menu) return;
 
  const triggers = menu.querySelectorAll('[on\\:click="#cart-drawer/open"], [aria-controls="cart-drawer"], a[href$="/cart"]');
  triggers.forEach((trigger) => {
    let badge = trigger.querySelector('.kv-header__cart-count');
    if (count <= 0) {
      badge?.remove();
      return;
    }
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'kv-header__cart-count';
      trigger.appendChild(badge);
    }
    badge.textContent = String(count);
    const label = trigger.getAttribute('aria-label');
    if (label && /panier|cart/i.test(label)) {
      trigger.setAttribute('aria-label', label.replace(/\d+/, String(count)));
    }
  });
}
 
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  /** @type {any} */ (event).promise
    ?.then((/** @type {any} */ result) => {
      const count = result?.cart?.totalQuantity ?? result?.detail?.itemCount;
      renderHeaderCount(Number(count));
    })
    .catch(() => {});
});
 
/* ==========================================================================
   4. Pairs well with — KAVORRI Smart engine
      Weights, categories and complementarity matrix are identical to
      sections/smart-look.liquid ("Kavorri Smart Complete").
   ========================================================================== */
 
const STOP = new Set(['and', 'or', 'the', 'a', 'an', 'of', 'for', 'with', 'light', 'lights', 'lighting', 'lamp', 'fixture']);
 
/** @param {any} v @returns {string[]} */
const norm = (v) => {
  if (v == null) return [];
  if (Array.isArray(v)) return v.flatMap(norm);
  if (typeof v === 'object') return Object.values(v).flatMap(norm);
  return String(v)
    .toLowerCase()
    .replace(/[&+]/g, ' and ')
    .replace(/[\/;,|–—-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
};
const toSet = (v) => new Set(norm(v).filter((t) => !STOP.has(t)));
const shared = (a, b) => {
  let n = 0;
  a.forEach((t) => b.has(t) && n++);
  return n;
};
 
const category = (value, type = '') => {
  const s = norm(value).join(' ') + ' ' + norm(type).join(' ');
  if (s.includes('outdoor') && (s.includes('wall') || s.includes('sconce'))) return 'outdoor_wall';
  if (s.includes('bollard') || s.includes('pathway')) return 'outdoor_bollard';
  if (s.includes('pillar') || s.includes('post light')) return 'outdoor_pillar';
  if (s.includes('spotlight') || s.includes('spot light')) return 'outdoor_spotlight';
  if (s.includes('chandelier')) return 'chandelier';
  if (s.includes('pendant')) return 'pendant';
  if (s.includes('ceiling')) return 'ceiling';
  if (s.includes('wall') || s.includes('sconce')) return 'indoor_wall';
  if (s.includes('floor')) return 'floor';
  if (s.includes('table')) return 'table';
  return 'other';
};
 
const profile = (o) => {
  const c = category(o.lighting_type, o.type);
  const hay = [norm(o.lighting_type).join(' '), norm(o.rooms).join(' '), norm(o.type).join(' ')].join(' ');
  return {
    category: c,
    env: c.startsWith('outdoor_') || /(outdoor|exterior|garden|patio|porch)/.test(hay) ? 'outdoor' : 'indoor',
    style: toSet(o.style),
    finish: toSet(o.finish),
    materials: toSet(o.materials),
    rooms: toSet(o.rooms),
    temp: toSet(o.colour_temperature),
    ip: toSet(o.ip_rating),
    install: toSet(o.installation_type),
    power: toSet(o.power_source),
  };
};
 
/** Complementarity matrix — identical to Smart Complete. */
const COMPLEMENT = {
  outdoor_wall: { outdoor_bollard: 42, outdoor_pillar: 40, outdoor_spotlight: 36, outdoor_wall: 7 },
  outdoor_bollard: { outdoor_wall: 42, outdoor_pillar: 38, outdoor_spotlight: 34, outdoor_bollard: 7 },
  outdoor_pillar: { outdoor_wall: 42, outdoor_bollard: 38, outdoor_spotlight: 34, outdoor_pillar: 7 },
  outdoor_spotlight: { outdoor_wall: 40, outdoor_bollard: 36, outdoor_pillar: 34, outdoor_spotlight: 7 },
  chandelier: { indoor_wall: 40, pendant: 32, ceiling: 28, floor: 22, table: 20, chandelier: 7 },
  pendant: { indoor_wall: 40, ceiling: 32, chandelier: 28, floor: 20, table: 18, pendant: 7 },
  ceiling: { indoor_wall: 40, pendant: 32, chandelier: 27, floor: 20, table: 18, ceiling: 7 },
  indoor_wall: { pendant: 38, ceiling: 34, chandelier: 30, floor: 24, table: 22, indoor_wall: 7 },
  floor: { table: 34, indoor_wall: 30, pendant: 22, floor: 7 },
  table: { floor: 34, indoor_wall: 28, pendant: 20, table: 7 },
};
 
/**
 * Smart Complete score of one candidate against one reference product (unchanged formula).
 */
function pairScore(ref, refProfile, cand, p) {
  const comp = (COMPLEMENT[refProfile.category] && COMPLEMENT[refProfile.category][p.category]) || 0;
  let s =
    comp +
    Math.min(shared(refProfile.style, p.style), 3) * 18 +
    Math.min(shared(refProfile.finish, p.finish), 2) * 20 +
    Math.min(shared(refProfile.materials, p.materials), 2) * 8 +
    Math.min(shared(refProfile.rooms, p.rooms), 2) * 7 +
    Math.min(shared(refProfile.temp, p.temp), 2) * 5 +
    Math.min(shared(refProfile.ip, p.ip), 1) * 5 +
    Math.min(shared(refProfile.install, p.install), 1) * 4 +
    Math.min(shared(refProfile.power, p.power), 1) * 4 +
    (refProfile.env === p.env ? 15 : -35);
 
  const ratio = ref.price > 0 && cand.price > 0 ? cand.price / ref.price : 1;
  if (ratio <= 1) s += 8;
  else if (ratio <= 1.25) s += 4;
  else if (ratio > 1.75) s -= 5;
  return s;
}
 
/**
 * Whole-cart ranking.
 * - best single-product score (Smart Complete formula)
 * - + synergy: 25% of the average score against the other cart products (capped at +12)
 * - − 15 per cart product already of the same fixture category (avoids over-representation)
 * - environment is mandatory: indoor-only carts never get outdoor pieces and vice versa
 */
export function rankForCart(cartItems, candidates, { minScore = 10, limit = 3, maxSame = 1 } = {}) {
  const refs = cartItems.map((raw) => ({ raw, p: profile(raw) }));
  if (!refs.length) return [];
 
  const cartEnvs = new Set(refs.map((r) => r.p.env));
  const cartCats = refs.reduce((acc, r) => ((acc[r.p.category] = (acc[r.p.category] || 0) + 1), acc), {});
  const cartIds = new Set(cartItems.map((x) => String(x.id)));
 
  const seen = new Set();
  const ranked = [];
 
  for (const cand of candidates) {
    const id = String(cand?.id ?? '');
    if (!id || seen.has(id) || cartIds.has(id) || cand.available === false) continue;
    seen.add(id);
 
    const p = profile(cand);
    if (!cartEnvs.has(p.env)) continue;
 
    const scores = refs.filter((r) => r.p.env === p.env).map((r) => pairScore(r.raw, r.p, cand, p));
    if (!scores.length) continue;
 
    scores.sort((a, b) => b - a);
    const best = scores[0];
    const others = scores.slice(1).filter((s) => s > 0);
    const synergy = others.length ? Math.min(12, Math.round((others.reduce((a, b) => a + b, 0) / others.length) * 0.25)) : 0;
    const overRep = (cartCats[p.category] || 0) * 15;
 
    const score = best + synergy - overRep;
    if (score < minScore) continue;
    ranked.push({ ...cand, _p: p, _score: score });
  }
 
  ranked.sort((a, b) => b._score - a._score);
 
  const out = [];
  const counts = {};
  for (const x of ranked) {
    if (out.length >= limit) break;
    const n = counts[x._p.category] || 0;
    if (n >= maxSame) continue;
    out.push(x);
    counts[x._p.category] = n + 1;
  }
  for (const x of ranked) {
    if (out.length >= limit) break;
    if (!out.includes(x)) out.push(x);
  }
  return out;
}
 
class KavorriCartPairs extends HTMLElement {
  static observedAttributes = ['data-key'];
 
  /** @type {MutationObserver | null} */
  #observer = null;
  #frame = 0;
  #busy = false;
 
  connectedCallback() {
    this.#observer = new MutationObserver(() => {
      if (!this.#busy) this.#schedule();
    });
    this.#observer.observe(this, { childList: true, subtree: true, characterData: true });
    this.#schedule();
  }
 
  disconnectedCallback() {
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }
 
  attributeChangedCallback() {
    if (this.isConnected) this.#schedule();
  }
 
  #schedule() {
    cancelAnimationFrame(this.#frame);
    // Two frames: let a Section Rendering API morph finish updating the JSON first.
    this.#frame = requestAnimationFrame(() => {
      this.#frame = requestAnimationFrame(() => this.render());
    });
  }
 
  #parse(node) {
    try {
      return JSON.parse(node?.textContent || 'null');
    } catch (_) {
      return null;
    }
  }
 
  #money(cents) {
    const currency = this.dataset.currency || 'GBP';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency }).format(
        Number(cents || 0) / 100
      );
    } catch (_) {
      return (Number(cents || 0) / 100).toFixed(2);
    }
  }
 
  render() {
    const list = this.querySelector('[data-kc-pairs-list]');
    if (!list) return;
 
    const cartItems = this.#parse(this.querySelector('[data-kc-cart]')) || [];
    const candidates = [];
    this.querySelectorAll('[data-kc-source]').forEach((node) => {
      const arr = this.#parse(node);
      if (Array.isArray(arr)) candidates.push(...arr);
    });
 
    const picks = rankForCart(cartItems, candidates, {
      minScore: Number(this.dataset.minScore || 10),
      limit: Number(this.dataset.limit || 3),
      maxSame: Number(this.dataset.maxSame || 1),
    });
 
    const signature = picks.map((x) => x.id).join(',');
    if (signature && list.dataset.signature === signature && list.children.length === picks.length) return;
 
    this.#busy = true;
    list.dataset.signature = signature;
    list.replaceChildren(...picks.map((x) => this.#card(x)));
    this.#observer?.takeRecords();
    this.#busy = false;
  }
 
  #card(x) {
    const row = document.createElement('div');
    row.className = 'kc-pair';
    row.dataset.productId = String(x.id);
 
    const media = document.createElement('a');
    media.className = 'kc-pair__media';
    media.href = x.url;
    media.tabIndex = -1;
    media.setAttribute('aria-hidden', 'true');
    if (x.image) {
      const img = document.createElement('img');
      img.src = x.image;
      img.alt = '';
      img.width = 72;
      img.height = 72;
      img.loading = 'lazy';
      media.appendChild(img);
    }
 
    const info = document.createElement('div');
    info.className = 'kc-pair__info';
    const title = document.createElement('a');
    title.className = 'kc-pair__title';
    title.href = x.url;
    title.textContent = x.title || '';
    const price = document.createElement('span');
    price.className = 'kc-pair__price';
    price.textContent = (x.varies ? 'From ' : '') + this.#money(x.price);
    info.append(title, price);
 
    let action;
    if (Number(x.variant_count) === 1 && x.variant_id) {
      action = document.createElement('button');
      action.type = 'button';
      action.className = 'kc-pair__add';
      action.textContent = 'Add';
      action.setAttribute('aria-label', `Add ${x.title || 'product'} to cart`);
      action.addEventListener('click', () => this.#add(x, /** @type {HTMLButtonElement} */ (action)));
    } else {
      // Variant selection required: never add a guessed variant.
      action = document.createElement('a');
      action.className = 'kc-pair__add';
      action.href = x.url;
      action.textContent = 'Choose';
      action.setAttribute('aria-label', `Choose options for ${x.title || 'product'}`);
    }
 
    row.append(media, info, action);
    return row;
  }
 
  async #add(x, button) {
    if (button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adding…';
 
    const variantId = Number(x.variant_id);
    const sectionIds = new Set([SECTION_ID]);
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
    });
 
    /** @type {any} */
    let deferred = null;
    try {
      deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'cart',
          lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }],
          promise: deferred.promise,
        })
      );
    } catch (error) {
      console.warn('[kavorri-cart-pairs] Could not dispatch cart event, falling back to a section refresh.', error);
      deferred = null;
    }
 
    try {
      const routes = /** @type {any} */ (window).Theme?.routes;
      const url = routes?.cart_update_url || `${(window.Shopify?.routes?.root || '/')}cart/update.js`;
      const response = await fetch(
        url,
        fetchConfig('json', {
          body: JSON.stringify({
            updates: { [variantId]: 1 },
            sections: Array.from(sectionIds).join(','),
            sections_url: window.location.pathname,
          }),
        })
      );
      const data = await response.json();
      if (!response.ok || data.status || data.errors) throw new Error(data.description || data.message || 'Add failed');
 
      if (deferred) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
          detail: {
            sections: data.sections,
            items: data.items,
            itemCount: data.item_count,
            source: 'kavorri-cart-pairs',
            didError: false,
          },
        });
      } else {
        const { morphSection } = await import('@theme/section-renderer');
        if (data.sections?.[SECTION_ID]) morphSection(SECTION_ID, data.sections[SECTION_ID], { mode: 'hydration' });
        renderHeaderCount(Number(data.item_count));
      }
    } catch (error) {
      deferred?.reject(error);
      button.disabled = false;
      button.removeAttribute('aria-busy');
      button.textContent = 'Unavailable';
      setTimeout(() => {
        if (button.isConnected) button.textContent = 'Add';
      }, 2200);
    }
  }
}
 
if (!customElements.get('kavorri-cart-pairs')) {
  customElements.define('kavorri-cart-pairs', KavorriCartPairs);
}
 
initOverlay();
syncSummaryAria();